import { useEffect, useState } from 'react';
import { ScreenSkeleton } from '../components/atoms/ScreenSkeleton';
import { useNavigate, useParams } from 'react-router';
import { TableOfContentsRenderer } from '../components/templates/TableOfContentsRenderer';
import { EquipmentModel } from '../data/types';
import { EquipmentType, TaskStatus } from '../data/enums';
import { Button } from '../components/atoms/Button';
import { ArrowLeft } from 'lucide-react';
import useInspectionCategoryType from '../hooks/useInspectionCategoryType';
import { ErrorBanner } from '../components/atoms/ErrorBanner';
import { useAssetMineLocationLevel } from '../hooks/useAssetMineLocationLevel';
import { ScreenHeading } from '../components/molecules/ScreenHeading';
import { useInspectionCached } from '../hooks/useInspectionCached';
import useInspectionUndercarriageIndicator from '../hooks/progressIndicators/useInspectionUndercarriageIndicator';

export const UndercarriageTableOfContentsScreen = () => {
  const { externalId } = useParams();
  const navigate = useNavigate();

  const inspection = useInspectionCached(externalId ?? '');
  const assetMineLocationState = useAssetMineLocationLevel({
    inspectionId: externalId ?? '',
  });

  const [equipment, setEquipment] = useState<EquipmentModel[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const undercarriageCategory = useInspectionCategoryType({
    inspection: inspection?.inspection,
  });

  useEffect(() => {
    async function getEquipment() {
      try {
        if (assetMineLocationState.isLoading) {
          return;
        }

        if (!assetMineLocationState.asset) {
          setError('Asset not found');
          return;
        }

        const equipments =
          assetMineLocationState.asset?.equipments
            .filter(
              (e) =>
                e.equipmentCategory.toLowerCase() ===
                EquipmentType.undercarriage.toLowerCase(),
            )
            .sort((a, b) => a.name.localeCompare(b.name)) ?? [];

        setEquipment(equipments);
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    }

    if (assetMineLocationState.asset) {
      getEquipment();
    }
  }, [
    assetMineLocationState.isLoading,
    assetMineLocationState.error,
    assetMineLocationState.asset,
  ]);

  const { sideIndicators } = useInspectionUndercarriageIndicator(
    externalId ?? '',
  );

  if (!inspection || assetMineLocationState.isLoading) {
    return <ScreenSkeleton fields={8} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  if (assetMineLocationState.error) {
    return <ErrorBanner message={assetMineLocationState.error} />;
  }

  if (!assetMineLocationState.asset) {
    return <ErrorBanner message="Asset not found" />;
  }

  return (
    <div className="flex flex-col">
      {/* ToC Content */}
      <TableOfContentsRenderer
        sections={[
          {
            id: 'equipment',
            order: 1,
            title: '',
            items:
              equipment?.map((equipment, index) => {
                const status = sideIndicators.find(
                  (indicator) =>
                    indicator.externalId === equipment.externalId &&
                    indicator.name === equipment.name,
                );
                return {
                  id: `equipment-${index}`,
                  order: index,
                  label: equipment.name,
                  status: status?.status ?? TaskStatus.notStarted,
                  url: `${equipment.name.toLocaleLowerCase().replace(' ', '-')}`,
                  enabled: true,
                  inspectionCategoryTypeId:
                    undercarriageCategory?.externalId ?? '',
                  inspectionId: externalId ?? '',
                };
              }) ?? [],
          },
        ]}
        header={
          <div className="mb-6">
            <ScreenHeading
              sticky
              title="Undercarriage condition"
              tooltipKey={'undercarriage-condition-screen-header'}
            />
          </div>
        }
        footer={
          <div className="flex flex-col items-center justify-center space-y-4 px-4">
            <Button
              variant="secondary"
              onClick={() => navigate(-1)}
              className="mt-8"
            >
              <ArrowLeft size={24} />
              Done
            </Button>
          </div>
        }
      />
    </div>
  );
};
